import { unsavedLeaveSheet } from "../components/ui";

type LeaveGuard = () => Promise<boolean>;

let leaveGuard: LeaveGuard | null = null;
let globalOnSave: (() => Promise<boolean>) | null = null;
let lastAllowedHash = "";

export type DirtyGuard = {
  clear: () => void;
  attemptLeave: (go: () => void, opts?: { onSave?: () => Promise<boolean> }) => Promise<void>;
  isDirty: () => boolean;
  markDirty: () => void;
};

export function setLeaveGuard(fn: LeaveGuard | null) {
  leaveGuard = fn;
}

export function clearLeaveGuard() {
  leaveGuard = null;
  globalOnSave = null;
}

/** Register a save handler for global navigation prompts (tab/hash changes). */
export function setGlobalSaveHandler(fn: (() => Promise<boolean>) | null) {
  globalOnSave = fn;
}

/** Serialize form controls for dirty comparison. */
export function serializeForm(form: HTMLFormElement): string {
  const fd = new FormData(form);
  const parts: string[] = [];
  for (const [k, v] of fd.entries()) {
    parts.push(`${k}=${typeof v === "string" ? v : "[file]"}`);
  }
  form.querySelectorAll("input[disabled][name], select[disabled][name], textarea[disabled][name]").forEach((el) => {
    const input = el as HTMLInputElement;
    parts.push(`${input.name}=${input.value}`);
  });
  return parts.sort().join("&");
}

async function promptLeave(
  isDirty: () => boolean,
  onSave?: (() => Promise<boolean>) | null
): Promise<"stay" | "leave" | "saved"> {
  if (!isDirty()) return "leave";
  const saveFn = onSave ?? globalOnSave;
  const choice = await unsavedLeaveSheet(Boolean(saveFn));
  if (choice === "stay") return "stay";
  if (choice === "save" && saveFn) {
    const ok = await saveFn();
    return ok ? "saved" : "stay";
  }
  return "leave";
}

/**
 * Watch a form for unsaved changes. Sets a global leave guard for tab/hash navigation.
 * Call `clear()` after successful save; use `attemptLeave(go)` for Back.
 */
export function attachFormGuard(form: HTMLFormElement): DirtyGuard {
  let baseline = serializeForm(form);
  let dirty = false;

  const refreshDirty = () => {
    dirty = serializeForm(form) !== baseline;
  };

  form.addEventListener("input", refreshDirty);
  form.addEventListener("change", refreshDirty);

  const isDirty = () => {
    refreshDirty();
    return dirty;
  };

  const ask = async () => {
    const result = await promptLeave(isDirty);
    return result !== "stay";
  };

  setLeaveGuard(ask);

  const guard: DirtyGuard = {
    isDirty,
    markDirty: () => {
      dirty = true;
    },
    clear: () => {
      dirty = false;
      baseline = serializeForm(form);
      clearLeaveGuard();
    },
    attemptLeave: async (go, opts) => {
      const result = await promptLeave(isDirty, opts?.onSave);
      if (result === "stay") return;
      if (result === "saved") guard.clear();
      else clearLeaveGuard();
      go();
    },
  };
  return guard;
}

/** @deprecated Use attachFormGuard */
export const attachUnsavedGuard = attachFormGuard;

/** Dirty guard for non-form UIs (e.g. logo editor). */
export function attachCustomGuard(opts: {
  isDirty: () => boolean;
  markDirty?: () => void;
  clear?: () => void;
}): DirtyGuard {
  let dirty = false;

  const isDirty = () => opts.isDirty() || dirty;

  const ask = async () => {
    const result = await promptLeave(isDirty);
    return result !== "stay";
  };

  setLeaveGuard(ask);

  const guard: DirtyGuard = {
    isDirty,
    markDirty: () => {
      dirty = true;
      opts.markDirty?.();
    },
    clear: () => {
      dirty = false;
      opts.clear?.();
      clearLeaveGuard();
    },
    attemptLeave: async (go, opts) => {
      const result = await promptLeave(isDirty, opts?.onSave);
      if (result === "stay") return;
      if (result === "saved") guard.clear();
      else clearLeaveGuard();
      go();
    },
  };
  return guard;
}

/** Call before handling a route change. Returns false if navigation should be aborted. */
export async function allowNavigation(): Promise<boolean> {
  if (!leaveGuard) return true;
  const ok = await leaveGuard();
  if (ok) clearLeaveGuard();
  return ok;
}

export function rememberAllowedHash(hash: string) {
  lastAllowedHash = hash || "#home";
}

export function getLastAllowedHash() {
  return lastAllowedHash || "#home";
}
